
import { Alert, Box, Button, Card, CardContent, Typography, CircularProgress, Chip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Dayjs } from "dayjs";
import { useState } from "react";
import { environment } from "../../../../environments/environment";
import DateInputFilter from "../../../../ui/components/date-input-filter/date-input-filter";
import { practicaServices } from "../../../services/practica.services";
import "./sempre-informes.scss";

interface TipoInforme {
  id: string;
  titulo: string;
  descripcion: string;
  icono: React.ReactNode;
  color: string;
  disponible: boolean;
}

const tiposInforme: TipoInforme[] = [
  {
    id: "sempre",
    titulo: "Exportación Archivo Mensual (txt)",
    descripcion: "Generar archivo de prácticas en formato SEMPRE para el período seleccionado",
    icono: <FileDownloadIcon sx={{ fontSize: 48 }} />,
    color: "#ed6c02", // Naranja SEMPRE
    disponible: true
  }
  // Resumen mensual oculto temporalmente
  // {
  //   id: "resumen",
  //   titulo: "Resumen Mensual", 
  //   descripcion: "Informe resumen de actividad mensual (próximamente disponible)",
  //   icono: <AssessmentIcon sx={{ fontSize: 48 }} />,
  //   color: "#2e7d32",
  //   disponible: false
  // }
];

export default function SempreInformes() {
  const [fetching, setFetching] = useState<boolean>(false);
  const [showAlertMessage, setShowAlertMessage] = useState<boolean>(false);
  const [textAlertMessage, setTextAlertMessage] = useState<string>("");
  const [monthSelected, setMonthSelected] = useState<number>();
  const [yearSelected, setYearSelected] = useState<number>();

  const handleDownloadSempre = async () => {
    if (!yearSelected || monthSelected === undefined) {
      setTextAlertMessage("Por favor seleccione el mes y año para exportar");
      setShowAlertMessage(true);
      return;
    }

    setFetching(true);
    try {
      let fecha = new Date(yearSelected, monthSelected, 1);
      fecha.setUTCHours(0, 0, 0, 0);
      const fechaISO = fecha.toISOString();
      const settings = await environment;
      
      const params = {
        fechaDesde: fechaISO,
        obraSocialId: settings.sempreId,
      };

      const response = await practicaServices.generarArchivoSempre(params);
      
      // Crear y descargar el archivo
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.setAttribute("download", `sempre_${yearSelected}_${monthSelected + 1}.txt`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
      setTextAlertMessage("Archivo SEMPRE generado y descargado exitosamente");
      setShowAlertMessage(true);
      
    } catch (error) {
      console.error("Error al generar archivo SEMPRE:", error);
      setTextAlertMessage("Error al generar el archivo SEMPRE. Intente nuevamente.");
      setShowAlertMessage(true);
    } finally {
      setFetching(false);
    }
  };

  const isDateSelected = yearSelected && monthSelected !== undefined;
  const selectedDateText = isDateSelected 
    ? `${getMonthName(monthSelected!)} ${yearSelected}`
    : "No seleccionado";

  function getMonthName(month: number): string {
    const months = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];
    return months[month];
  }

  return (
    <div className="sempre-informes">
      <div className="header-section">
        <h1>Informes y Exportaciones SEMPRE</h1>
        <p>Genere y descargue informes del sistema SEMPRE</p>
      </div>

      {showAlertMessage && (
        <Alert 
          severity={textAlertMessage.includes("Error") ? "error" : "success"} 
          onClose={() => setShowAlertMessage(false)}
          sx={{ mb: 3 }}
        >
          {textAlertMessage}
        </Alert>
      )}

      {/* Sección de filtros de fecha */}
      <Card className="filters-card">
        <CardContent>
          <Typography variant="h6" component="h2" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CalendarMonthIcon sx={{ mr: 1, color: '#4caf50' }} />
            Seleccionar Período
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <DateInputFilter
              name="mesAno"
              hiddenDay={true}
              onChange={(value: Dayjs) => {
                if (value !== null) {
                  setMonthSelected(value.get("month"));
                  setYearSelected(value.get("year"));
                } else {
                  setMonthSelected(undefined);
                  setYearSelected(undefined);
                }
              }}
            />
            
            <Chip 
              label={`Período: ${selectedDateText}`}
              color={isDateSelected ? "primary" : "default"}
              variant={isDateSelected ? "filled" : "outlined"}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            Seleccione el mes y año del cual desea generar los informes
          </Typography>
        </CardContent>
      </Card>

      {/* Grid de tipos de informes */}
      <div className="informes-grid">
        {tiposInforme.map((tipo) => (
          <Card 
            key={tipo.id} 
            className="informe-card" 
            sx={{ 
              borderColor: tipo.color,
              opacity: tipo.disponible ? 1 : 0.6,
              position: 'relative'
            }}
          >
            {!tipo.disponible && (
              <div className="coming-soon-badge">
                Próximamente
              </div>
            )}
            
            <CardContent>
              <div className="informe-header">
                <div className="informe-icon" style={{ color: tipo.color }}>
                  {tipo.icono}
                </div>
                <div className="informe-info">
                  <Typography variant="h6" component="h3" sx={{ color: tipo.color, fontWeight: 600 }}>
                    {tipo.titulo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tipo.descripcion}
                  </Typography>
                </div>
              </div>

              <div className="informe-actions">
                {tipo.id === 'sempre' && (
                  <Button
                    variant="contained"
                    startIcon={fetching ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    onClick={handleDownloadSempre}
                    disabled={!isDateSelected || fetching}
                    fullWidth
                    sx={{ 
                      backgroundColor: '#ff9800', // Naranja más tenue
                      '&:hover': {
                        backgroundColor: '#f57c00', // Naranja un poco más oscuro en hover
                      },
                      '&:disabled': {
                        backgroundColor: '#ccc'
                      }
                    }}
                  >
                    {fetching ? 'Generando...' : 'Generar y Descargar'}
                  </Button>
                )}
                
                {!tipo.disponible && (
                  <Button
                    variant="outlined"
                    disabled
                    fullWidth
                    sx={{ 
                      borderColor: '#ccc',
                      color: '#666'
                    }}
                  >
                    No Disponible
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="info-card">
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            Información sobre los Informes
          </Typography>
          
          <div className="info-list">
            <div className="info-item">
              <Typography variant="subtitle2">Exportación SEMPRE:</Typography>
              <Typography variant="body2">
                Genera un archivo de texto (.txt) con todas las prácticas del período seleccionado en el formato requerido por SEMPRE.
              </Typography>
            </div>
            
            <div className="info-item">
              <Typography variant="subtitle2">Resumen Mensual:</Typography>
              <Typography variant="body2">
                Informe detallado de la actividad mensual con estadísticas y métricas (próximamente disponible).
              </Typography>
            </div>
          </div>

          <div className="important-notes">
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Notas Importantes:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Es obligatorio seleccionar el mes y año para generar informes
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Los archivos se descargan automáticamente al generarse
            </Typography>
            <Typography variant="body2">
              • Los informes incluyen todas las prácticas registradas en el período seleccionado
            </Typography>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
